// src/pages/InscripcionPublica.tsx
import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { QRCodeCanvas } from "qrcode.react";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import FooterTop from "../../components/footer-top";
import Footer from "../../components/footer";
import UserNav from "../../components/navbar/user-nav";
import { Link } from "react-router-dom";

const schema = yup.object({
  taller_doc_id: yup.string().required("Selecciona un taller"),
  nombre: yup.string().required("El nombre es obligatorio"),
  correo: yup.string().email("Correo inválido").required("El correo es obligatorio"),
  telefono: yup.string().required("El teléfono es obligatorio"),
  inmobiliaria: yup.string().required("La inmobiliaria es obligatoria"),
});

export default function InscripcionPublica() {
  const [talleres, setTalleres] = useState<any[]>([]);
  const [qrGenerado, setQrGenerado] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchTalleres = async () => {
      const hoy = new Date();
      const q = query(
        collection(db, "taller"),
        where("publico", "==", "publico")
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((t: any) => t.fechaTermino.toDate() > hoy)
        .sort((a: any, b: any) => a.fechaInicio.toDate().getTime() - b.fechaInicio.toDate().getTime());
      setTalleres(data);
    };
    fetchTalleres();
  }, []);

  const onSubmit = async (data: any) => {
    const { correo, taller_doc_id } = data;

    // Validar duplicado
    const q = query(
      collection(db, "externo_inscripcion"),
      where("correo", "==", correo),
      where("taller_doc_id", "==", taller_doc_id)
    );
    const dupSnap = await getDocs(q);
    if (!dupSnap.empty) {
      Swal.fire({
        icon: "warning",
        title: "Ya estás registrado",
        text: "Este correo ya fue inscrito en el taller seleccionado.",
      });
      return;
    }

    // Si no hay duplicado, registrar
    const qrCode = uuidv4();
    const payload = {
      ...data,
      fechaInscripcion: Timestamp.now(),
      completado: false,
      certificado: false,
      codigo_qr: qrCode,
      estatus_pago: "pendiente",
    };

    try {
      await addDoc(collection(db, "externo_inscripcion"), payload);
      setQrGenerado(qrCode);
      Swal.fire({
        icon: "success",
        title: "¡Registro exitoso!",
        text: "Tu inscripción fue registrada. Guarda el código QR.",
        confirmButtonText: "Ver QR",
      });
      reset();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo registrar. Intenta más tarde.",
      });
    }
  };

  return (
    <>
      <UserNav />
      <div className="container mt-5">
        <h2 className="text-center mb-4">Registro a Talleres (Público en general)</h2>

        <div className="alert alert-info text-center">
          ¿Ya tienes cuenta?{" "}
          <Link to="/inscripcion-privada">
            Inicia sesión aquí para inscribirte como usuario registrado.
          </Link>
        </div>

        {talleres.length === 0 ? (
          <p className="text-center">No hay talleres públicos disponibles.</p>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="card p-4">
            <div className="form-group mb-3">
              <label>Taller <span className="text-danger">*</span></label>
              <select className="form-control" {...register("taller_doc_id")}>
                <option value="">Selecciona un taller</option>
                {talleres.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.nombre} – {t.fechaInicio.toDate().toLocaleDateString()}
                  </option>
                ))}
              </select>
              <small className="text-danger">{errors.taller_doc_id?.message}</small>
            </div>

            <div className="row">
              <div className="form-group col-md-6">
                <label>Nombre <span className="text-danger">*</span></label>
                <input className="form-control" {...register("nombre")} />
                <small className="text-danger">{errors.nombre?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Correo <span className="text-danger">*</span></label>
                <input className="form-control" {...register("correo")} />
                <small className="text-danger">{errors.correo?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Teléfono <span className="text-danger">*</span></label>
                <input className="form-control" {...register("telefono")} />
                <small className="text-danger">{errors.telefono?.message}</small>
              </div>
              <div className="form-group col-md-6">
                <label>Inmobiliaria <span className="text-danger">*</span></label>
                <input className="form-control" {...register("inmobiliaria")} />
                <small className="text-danger">{errors.inmobiliaria?.message}</small>
              </div>
            </div>

            <button type="submit" className="btn btn-primary mt-3">
              Inscribirme
            </button>
          </form>
        )}

        {qrGenerado && (
          <div className="text-center mt-4">
            <h5>Tu Código QR:</h5>
            <QRCodeCanvas value={qrGenerado} size={200} />
            <p className="mt-2">Guárdalo para presentarlo el día del evento.</p>
          </div>
        )}
      </div>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
